/** Phase 2 data: lead edits (statuses/notes) + tasks, and the write door.
 *
 * READS: rec_lead_fields + rec_tasks via PostgREST under the org-scoped
 * SELECT policies (MIGRATION_CRM2_PHASE2.sql). Cached in IndexedDB for
 * paint-first, same discipline as leads.
 *
 * WRITES: ONE door — the crm2_apply_ops RPC. Org comes from the JWT server-
 * side; v2 never names an org id. Mutations are never blind-retried by the
 * http layer (attempts: 1) — the OUTBOX owns retries, because only it knows
 * the ops are idempotent.
 */
import { http } from "../../lib/http";
import { moneyExact } from "../../lib/money";
import { idbGet, idbSet } from "../../lib/idb";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../../config";
import type { RecOp } from "../../lib/outbox";
import { natKey, taskKey } from "../../lib/natkey";

export type LeadFields = Record<string, unknown>;
export type EditsMap = Map<string, LeadFields>;

export interface TaskRec {
  natKey: string;
  title: string;
  status: string | null;
  who: string | null;
  due: string | null;
  pri: string | null;
  deal: string | null;
  stage: string | null; // deal pipeline stage this auto task belongs to
  auto: boolean; //        v1 WF_STAGE_TASKS-generated checklist task
}

/** v1's completion vocabulary is st='completed' / 'pending' (transactions.js
 * toggleTask). v2 wrote 'done' for a while — read BOTH so nothing looks
 * wrong, but every new write uses v1's words. */
export const isTaskDone = (status: string | null | undefined): boolean =>
  status === "completed" || status === "done";

const EDITS_CACHE = "lead_edits_v1";
const TASKS_CACHE = "tasks_v1";
const PAGE = 1000;

const authHeaders = (token: string) => ({
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${token}`,
});

/** v1's compiled-in custom statuses — supabase-sync.js `const STATUSES=[...]`,
 * verbatim and in order. These are Tom's own pipeline stages; they are NOT in
 * rec_org_settings (checked: v1 hard-codes them), so v2 ports the constant. */
export const V1_CUSTOM_STATUSES = [
  "Pre-Check", "Needs Address", "Rental", "On Market", "Follow-Up",
  "In Negotiation", "Make Offer", "Offer Accepted", "Contract Signed", "Closed",
  "Deal Cancelled", "Snipe", "Need To Comp", "Comped", "Qualified",
  "Offer Sent", "Under Contract", "Not Interested", "Dead Deal", "Sold",
];

/** Every status a picker offers = v1's base set (leads-list.js _stageish)
 * followed by the custom list, deduped — mirrors v1's detail chip:
 * [...new Set([base…, ...STATUSES])]. */
export const STATUS_OPTIONS = [
  ...new Set([
    "New", "Needs Review", "Needs Address", "Qualified", "Not Qualified", "Disqualified",
    ...V1_CUSTOM_STATUSES,
  ]),
];

export const statusOf = (f: LeadFields | undefined): string => {
  const qual = f?.["qual"], stage = f?.["stage"];
  return (typeof qual === "string" && qual) || (typeof stage === "string" && stage) || "Needs Review";
};

export const notesOf = (f: LeadFields | undefined): string => {
  const n = f?.["notes"];
  return typeof n === "string" ? n : "";
};

/** v1 stores money edit values as display-ready strings ("$300,000") in
 * LEAD_EDITS — compedArv wins over listedArv, and `arv` is the mirror key
 * v1's list fallback reads. These helpers pin that contract. */
export const fmtMoneyEdit = (n: number): string => moneyExact(n);

export const parseMoneyEdit = (v: unknown): number | null => {
  if (typeof v === "number") return Number.isFinite(v) && v > 0 ? v : null;
  if (typeof v !== "string") return null;
  const f = parseFloat(v.replace(/[^0-9.]/g, ""));
  return Number.isFinite(f) && f > 0 ? f : null;
};

/** Effective ARV override for a lead's edits (comped beats listed beats arv). */
export const arvOverrideOf = (f: LeadFields | undefined): number | null =>
  parseMoneyEdit(f?.["compedArv"]) ?? parseMoneyEdit(f?.["listedArv"]) ?? parseMoneyEdit(f?.["arv"]);

/** 🔥 HOT reply — v1 _leadBadges: the Text Agent stamps edits.agentHot
 * (epoch ms) on a hot-classified reply; the badge shows for 48h. */
export const HOT_WINDOW_MS = 172800000;
export const isHotReply = (f: LeadFields | undefined, now: number): boolean => {
  const t = f?.["agentHot"];
  return typeof t === "number" && t > 0 && now - t < HOT_WINDOW_MS;
};

// ---- reads ------------------------------------------------------------------

/** Page through a PostgREST query until a short page. PostgREST silently
 * clamps any `limit` to 1000 server-side — a single-shot fetch on a table
 * that can exceed 1000 rows is exactly the bug that hid 2,540 of Tom's
 * contacts (2026-07-23). Exported so every list fetch uses this. */
export async function pageAll<T>(token: string, path: string, signal?: AbortSignal): Promise<T[]> {
  const rows: T[] = [];
  for (let off = 0; ; off += PAGE) {
    const page = await http<T[]>(`${SUPABASE_URL}/rest/v1/${path}&limit=${PAGE}&offset=${off}`, {
      headers: authHeaders(token),
      signal,
      timeoutMs: 20000,
    });
    if (!Array.isArray(page)) break;
    rows.push(...page);
    if (page.length < PAGE) break;
    if (off > 50 * PAGE) break; // hard ceiling — something is wrong
  }
  return rows;
}

export async function cachedEdits(): Promise<EditsMap | null> {
  const c = await idbGet<{ at: number; entries: [string, LeadFields][] }>(EDITS_CACHE);
  if (!c || !Array.isArray(c.entries)) return null;
  return new Map(c.entries);
}

export async function fetchEdits(token: string, signal?: AbortSignal): Promise<EditsMap> {
  const rows = await pageAll<{ nat_key?: unknown; fields?: unknown }>(
    token,
    "rec_lead_fields?select=nat_key,fields&deleted_at=is.null&order=updated_at.desc,id.desc",
    signal,
  );
  const map: EditsMap = new Map();
  for (const r of rows) {
    if (typeof r.nat_key !== "string" || !r.nat_key) continue;
    if (r.fields === null || typeof r.fields !== "object" || Array.isArray(r.fields)) continue;
    if (!map.has(r.nat_key)) map.set(r.nat_key, r.fields as LeadFields);
  }
  await idbSet(EDITS_CACHE, { at: Date.now(), entries: [...map.entries()] });
  return map;
}

export function narrowTask(row: Record<string, unknown>): TaskRec | null {
  const nk = row["nat_key"], title = row["title"];
  if (typeof nk !== "string" || !nk) return null;
  if (typeof title !== "string" || !title.trim()) return null;
  const s = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
  return {
    natKey: nk,
    title: title.trim(),
    status: s(row["status"]),
    who: s(row["who"]),
    due: s(row["due"]),
    pri: s(row["pri"]),
    deal: s(row["deal"]),
    stage: s(row["stage"]),
    auto: row["auto"] === true,
  };
}

export async function cachedTasks(): Promise<TaskRec[] | null> {
  const c = await idbGet<{ at: number; tasks: TaskRec[] }>(TASKS_CACHE);
  return c && Array.isArray(c.tasks) ? c.tasks : null;
}

export async function fetchTasks(token: string, signal?: AbortSignal): Promise<{ tasks: TaskRec[]; dropped: number }> {
  const rows = await pageAll<Record<string, unknown>>(
    token,
    "rec_tasks?select=nat_key,title,status,who,due,pri,deal,stage,auto&deleted_at=is.null&order=updated_at.desc,id.desc",
    signal,
  );
  let dropped = 0;
  const tasks: TaskRec[] = [];
  const seen = new Set<string>();
  for (const r of rows) {
    const t = narrowTask(r);
    if (!t) {
      dropped++;
      continue;
    }
    if (seen.has(t.natKey)) continue;
    seen.add(t.natKey);
    tasks.push(t);
  }
  await idbSet(TASKS_CACHE, { at: Date.now(), tasks });
  return { tasks, dropped };
}

// ---- the write door -----------------------------------------------------------

/** POST the ops batch to the RPC. attempts:1 — the outbox owns retries. */
export async function sendOps(token: string, ops: RecOp[]): Promise<void> {
  // `intent` is client-side bookkeeping for ledger merges — the door has a
  // strict op shape, so it must never see it.
  const wire = ops.map((o) => {
    if (o.intent === undefined) return o;
    const { intent: _i, ...rest } = o;
    return rest;
  });
  await http(`${SUPABASE_URL}/rest/v1/rpc/crm2_apply_ops`, {
    method: "POST",
    headers: authHeaders(token),
    body: { ops: wire },
    attempts: 1,
    timeoutMs: 15000,
  });
}

// ---- op builders (the ONLY places op shapes are written) ---------------------

/** Status: v1 sets BOTH qual and stage on a quick status change — so do we. */
export function opSetStatus(address: string, status: string): RecOp {
  return { s: "lead_fields", op: "patch", k: natKey(address), d: { qual: status, stage: status } };
}

export function opSetNotes(address: string, notes: string): RecOp {
  return { s: "lead_fields", op: "patch", k: natKey(address), d: { notes } };
}

/** ARV override: writes the specific key v1's detail edits (listedArv or
 * compedArv) as a "$X,XXX" string, PLUS the `arv` mirror with the effective
 * value so v1's list fallback and v2 agree. Passing null clears the key
 * (jsonb merge: null overwrites) and re-mirrors from what remains. */
export function opSetArv(
  address: string,
  kind: "listedArv" | "compedArv",
  value: number | null,
  existing: LeadFields | undefined,
): RecOp {
  const next: LeadFields = { ...(existing ?? {}) };
  if (value === null) delete next[kind];
  else next[kind] = fmtMoneyEdit(value);
  const eff = parseMoneyEdit(next["compedArv"]) ?? parseMoneyEdit(next["listedArv"]);
  const d: Record<string, unknown> = {
    [kind]: value === null ? null : fmtMoneyEdit(value),
    arv: eff === null ? null : fmtMoneyEdit(eff),
  };
  return { s: "lead_fields", op: "patch", k: natKey(address), d };
}

/** Generic LEAD_EDITS field write — key must come from LEAD_DETAIL_FIELDS
 * (v1's editable-field registry) so v2 can never invent keys v1 won't read.
 * null clears the field. */
export function opSetField(address: string, key: string, value: string | null): RecOp {
  return { s: "lead_fields", op: "patch", k: natKey(address), d: { [key]: value } };
}

// ---- Seller contact card (v1 leads-list.js saveSeller/clearSeller) -----------
// edits.seller = {name,role,phone,email,company} + edits.sellerCleared flag. The
// Deal Contacts sidebar (Parity 5b) reads this same object off the linked lead.

export interface SellerContact { name: string; role: string; phone: string; email: string; company: string; }
export const SELLER_ROLES = ["Wholesaler", "Agent", "Seller", "Title Company", "Lender", "Other"];

/** v1 sellerVal: the seller object, unless explicitly cleared. */
export function sellerOf(fields: Record<string, unknown> | undefined): SellerContact | null {
  if (!fields || fields["sellerCleared"] === true) return null;
  const s = fields["seller"];
  if (!s || typeof s !== "object" || Array.isArray(s)) return null;
  const o = s as Record<string, unknown>;
  const g = (k: string) => (typeof o[k] === "string" ? (o[k] as string) : "");
  const c: SellerContact = { name: g("name"), role: g("role") || "Wholesaler", phone: g("phone"), email: g("email"), company: g("company") };
  return c.name || c.phone || c.email || c.company ? c : null;
}

/** Save the whole seller object (rep:['seller'] → replace, not deep-merge, so
 * cleared sub-fields really clear). Phone normalized like v1's saveSeller. */
export function opSaveSeller(address: string, seller: SellerContact): RecOp {
  const clean: SellerContact = { ...seller, phone: seller.phone ? normPhone(seller.phone) || seller.phone : "" };
  return { s: "lead_fields", op: "patch", k: natKey(address), d: { seller: clean as unknown as Record<string, unknown>, sellerCleared: false }, rep: ["seller"] };
}

/** v1 clearSeller: seller=null + sellerCleared=true. */
export function opClearSeller(address: string): RecOp {
  return { s: "lead_fields", op: "patch", k: natKey(address), d: { seller: null, sellerCleared: true } };
}

// ---- Media links (v1 leads-list.js edits.mediaLinks) ------------------------
// User-added photo/video links live in edits.mediaLinks so they survive
// scraped-lead rebuilds (scraped media stay on the row's photos). [{label,url}].

export interface MediaLink { label: string; url: string; }

/** Read the user-added media links off a lead's fields (edits.mediaLinks). */
export function mediaLinksOf(fields: Record<string, unknown> | undefined): MediaLink[] {
  const v = fields?.["mediaLinks"];
  if (!Array.isArray(v)) return [];
  const out: MediaLink[] = [];
  for (const e of v) {
    if (typeof e === "string") { const u = e.trim(); if (u) out.push({ label: "Photos", url: u }); continue; }
    if (!e || typeof e !== "object") continue;
    const o = e as Record<string, unknown>;
    const url = (typeof o["url"] === "string" ? o["url"] : "").trim();
    if (!url) continue;
    out.push({ label: (typeof o["label"] === "string" ? o["label"] : "").trim() || "Photos", url });
  }
  return out;
}

/** Save the whole mediaLinks array (rep:['mediaLinks'] → replace wholesale, so a
 * removed link really clears; v1 stores the array under edits.mediaLinks). */
export function opSaveMediaLinks(address: string, links: MediaLink[]): RecOp {
  return { s: "lead_fields", op: "patch", k: natKey(address), d: { mediaLinks: links as unknown as Record<string, unknown> }, rep: ["mediaLinks"] };
}

// ---- Team → Linked Contacts + Activity (v1 detail-arv.js teamPanel + activityPanel)
// Both are DERIVED read-only views over the lead's fields/row — v1 synthesizes
// them from edits (listing agent + owner) and the scrape (source/photos/owner).

export interface LinkedContact { name: string; role: string; phone: string; }

/** v1 teamPanel Linked Contacts: listing agent (dispName/dispCompany/dispPhone)
 * + owner (owner/ownerPhone), pulled from the lead's edits. */
export function leadLinkedContacts(fields: Record<string, unknown> | undefined): LinkedContact[] {
  const g = (k: string) => (typeof fields?.[k] === "string" ? (fields[k] as string).trim() : "");
  const rows: LinkedContact[] = [];
  const dispName = g("dispName"), dispPhone = g("dispPhone"), dispCompany = g("dispCompany");
  if (dispName || dispPhone) rows.push({ name: dispName || "Listing Agent", role: dispCompany || "Agent", phone: dispPhone });
  const owner = g("owner"), ownerPhone = g("ownerPhone");
  if (ownerPhone) rows.push({ name: owner || "Owner", role: "Owner", phone: ownerPhone });
  return rows;
}

/** v1 activityPanel: derived rows from the scrape + county match (NOT a
 * timestamped audit). Generic input so it needn't import the Lead type. */
export function leadActivity(a: { scraped: boolean; source: string; photos: number; owner: string }): string[] {
  if (!a.scraped) return [];
  const rows: string[] = [];
  rows.push(`Imported from ${a.source || "scraper"}`);
  if (a.photos > 0) rows.push(`${a.photos} photo${a.photos === 1 ? "" : "s"} pulled from listing`);
  if (a.owner) rows.push(`County record matched: ${a.owner}`);
  return rows;
}

/** v1's phone normalizer (auth-users.js _normNum, verbatim semantics):
 * strip to digits/+, 11-with-1 → +…, 10 → +1…, unknown formats stay EXACTLY
 * as typed (slicing invented a wrong number once — 2026-07-06). commitEdit
 * applies it to any key matching /phone/i — so do we. */
export function normPhone(n: string): string {
  if (!n) return "";
  const s = String(n).replace(/[^\d+]/g, "");
  if (!s) return "";
  if (s[0] === "+") return s;
  if (s.length === 11 && s[0] === "1") return "+" + s;
  if (s.length === 10) return "+1" + s;
  return n;
}

/** v1's efSel option lists (leads-list.js OPT_*), verbatim. */
export const OPT_PTYPE = ["Single Family", "Townhouse", "Condo", "Duplex", "Triplex", "Quadplex", "Multi-Family", "Mobile / Manufactured", "Vacant Land", "Commercial"];
export const OPT_LABEL = ["Flip", "Rental", "Multi-Family", "BRRRR", "Wholesale"];
export const OPT_FLOOD = ["X (minimal risk)", "AE", "A", "AH", "AO", "VE", "D", "None / Unknown"];
export const OPT_WATER = ["City Water", "Well", "Community", "None"];
export const OPT_SEWER = ["Sewer", "Septic"];
export const OPT_PARKING = ["Garage", "1-Car Garage", "2-Car Garage", "3-Car Garage", "Carport", "Driveway", "Street", "None"];
export const OPT_CONSTR = ["Wood Frame", "Block / Masonry", "Concrete", "Brick", "Stucco", "Steel"];
export const OPT_FOUND_TYPE = ["Slab", "Crawl Space", "Basement", "Pier & Beam"];
export const OPT_ROOF = ["Shingle", "Tile", "Metal", "Flat / Membrane", "Other"];
export const OPT_ARCH = ["Ranch", "Colonial", "Contemporary", "Traditional", "Craftsman", "Mediterranean", "Bungalow", "Other"];
export const OPT_COND = ["Good", "Okay", "Bad"];
export const OPT_YESNO = ["Yes", "No"];
export const OPT_SEVERITY = ["None", "Minor", "Major"];
export const OPT_OCCUPANCY = ["Vacant", "Owner-Occupied", "Tenant-Occupied", "Unknown"];
export const OPT_PLUMB = ["Copper", "PEX", "CPVC", "PVC", "Galvanized Steel", "Polybutylene", "Brass", "Cast Iron", "Lead", "Mixed"];
export const OPT_SEWERLINE = ["PVC", "ABS", "Cast Iron", "Clay / Vitrified Clay", "Orangeburg", "Concrete", "Copper", "Galvanized", "Unknown"];
export const OPT_ELEC = ["Updated / 200A", "150A", "100A", "Fuses / Older", "Knob & Tube", "Needs Update"];

/** v1's lead-detail editable fields, mined from leads-list.js ef()/efSel()
 * calls. kind money = "$X,XXX" strings (v1's _MONEY_EDIT_KEYS); text = plain
 * string (phone-ish keys normalize via normPhone); select = v1's exact
 * option lists above. */
export interface LeadDetailField {
  key: string;
  label: string;
  kind: "money" | "text" | "select";
  group: "financials" | "property" | "condition" | "disposition" | "title";
  opts?: string[];
}

export const LEAD_DETAIL_FIELDS: LeadDetailField[] = [
  { key: "offerPrice", label: "Offer Price", kind: "money", group: "financials" },
  { key: "contractPrice", label: "Contract Price", kind: "money", group: "financials" },
  { key: "lastSold", label: "Last Sold Price", kind: "money", group: "financials" },
  { key: "hoa", label: "HOA Fee", kind: "money", group: "financials" },
  { key: "taxAmount", label: "Annual Tax", kind: "money", group: "financials" },
  { key: "mortgageLeft", label: "Mortgage Left", kind: "money", group: "financials" },
  { key: "assessed", label: "Assessed Value", kind: "money", group: "financials" },
  { key: "countyTax", label: "County Tax", kind: "money", group: "financials" },
  { key: "contractExpires", label: "Contract Expires", kind: "text", group: "financials" },
  { key: "pricePerSqft", label: "Avg $/Sqft", kind: "text", group: "financials" },
  { key: "offerMade", label: "Offer Made", kind: "select", group: "financials", opts: OPT_YESNO },
  { key: "offerAccepted", label: "Offer Accepted", kind: "select", group: "financials", opts: OPT_YESNO },
  { key: "owner", label: "Owner", kind: "text", group: "property" },
  { key: "parcel", label: "Parcel #", kind: "text", group: "property" },
  { key: "yearBuilt", label: "Year Built", kind: "text", group: "property" },
  { key: "published", label: "Listing Date", kind: "text", group: "property" },
  { key: "daysOnZillow", label: "Days on Zillow", kind: "text", group: "property" },
  { key: "lotSize", label: "Lot Size", kind: "text", group: "property" },
  { key: "lotAcres", label: "Lot Acres", kind: "text", group: "property" },
  { key: "houseSqft", label: "House Sqft", kind: "text", group: "property" },
  { key: "totalSqft", label: "Total Sqft", kind: "text", group: "property" },
  { key: "countySqft", label: "Living Sqft (County)", kind: "text", group: "property" },
  { key: "subdivision", label: "Subdivision", kind: "text", group: "property" },
  { key: "legalDesc", label: "Land Description", kind: "text", group: "property" },
  { key: "ptype", label: "Property Type", kind: "select", group: "property", opts: OPT_PTYPE },
  { key: "label", label: "Property Label", kind: "select", group: "property", opts: OPT_LABEL },
  { key: "archStyle", label: "Architectural Style", kind: "select", group: "property", opts: OPT_ARCH },
  { key: "construction", label: "Construction", kind: "select", group: "property", opts: OPT_CONSTR },
  { key: "parking", label: "Parking", kind: "select", group: "property", opts: OPT_PARKING },
  { key: "garage", label: "Has Garage", kind: "select", group: "property", opts: OPT_YESNO },
  { key: "pool", label: "Has Pool", kind: "select", group: "property", opts: OPT_YESNO },
  { key: "occupancy", label: "Occupancy", kind: "select", group: "property", opts: OPT_OCCUPANCY },
  { key: "flood", label: "Flood Zone", kind: "select", group: "property", opts: OPT_FLOOD },
  { key: "water", label: "Water Source", kind: "select", group: "property", opts: OPT_WATER },
  { key: "septic", label: "Septic/Sewer", kind: "select", group: "property", opts: OPT_SEWER },
  { key: "sewerLine", label: "Sewer Line Type", kind: "select", group: "property", opts: OPT_SEWERLINE },
  { key: "roofType", label: "Roof Type", kind: "select", group: "condition", opts: OPT_ROOF },
  { key: "foundation", label: "Foundation Type", kind: "select", group: "condition", opts: OPT_FOUND_TYPE },
  { key: "roofAge", label: "Roof Age", kind: "text", group: "condition" },
  { key: "hvacAge", label: "HVAC Age", kind: "text", group: "condition" },
  { key: "whAge", label: "Water Heater Age", kind: "text", group: "condition" },
  { key: "roofCond", label: "Roof Condition", kind: "select", group: "condition", opts: OPT_COND },
  { key: "hvacCond", label: "HVAC Condition", kind: "select", group: "condition", opts: OPT_COND },
  { key: "whCond", label: "WHH Condition", kind: "select", group: "condition", opts: OPT_COND },
  { key: "elec", label: "Electric", kind: "select", group: "condition", opts: OPT_ELEC },
  { key: "plumb", label: "Plumbing Type", kind: "select", group: "condition", opts: OPT_PLUMB },
  { key: "found", label: "Foundation Issues", kind: "select", group: "condition", opts: OPT_YESNO },
  { key: "structural", label: "Structural Issues", kind: "select", group: "condition", opts: OPT_YESNO },
  { key: "sink", label: "Sinkhole", kind: "select", group: "condition", opts: OPT_YESNO },
  { key: "termites", label: "Termite Issues", kind: "select", group: "condition", opts: OPT_SEVERITY },
  { key: "foundN", label: "Foundation Notes", kind: "text", group: "condition" },
  { key: "dispCompany", label: "Dispo Company", kind: "text", group: "disposition" },
  { key: "dispName", label: "Dispo Name", kind: "text", group: "disposition" },
  { key: "dispPhone", label: "Dispo Phone", kind: "text", group: "disposition" },
  { key: "dispEmail", label: "Dispo Email", kind: "text", group: "disposition" },
  { key: "titleCo", label: "Title Company", kind: "text", group: "title" },
  { key: "titlePhone", label: "Title Phone", kind: "text", group: "title" },
  { key: "titleEmail", label: "Title Email", kind: "text", group: "title" },
];

/** Task upsert: d mirrors v1's task object keys (t/st/who/due/pri/deal). */
export function opPutTask(t: { key: string; title: string; status?: string; who?: string; due?: string; pri?: string; deal?: string; stage?: string; auto?: boolean }): RecOp {
  return {
    s: "tasks",
    op: "put",
    k: t.key,
    d: {
      t: t.title,
      st: t.status ?? "pending",
      who: t.who ?? "",
      due: t.due ?? "",
      pri: t.pri ?? "",
      deal: t.deal ?? "",
      stage: t.stage ?? "",
      auto: t.auto ?? false,
    },
  };
}

export function opPatchTask(key: string, patch: { st?: string; who?: string; due?: string; pri?: string }): RecOp {
  return { s: "tasks", op: "patch", k: key, d: patch };
}

export function opDelTask(key: string): RecOp {
  return { s: "tasks", op: "del", k: key };
}


// ---- v1 stage workflow checklists (transactions.js WF_STAGE_TASKS) -----------

/** Auto-created tasks per pipeline stage, verbatim from v1. Stage names are
 * the TXN_COLS/TXN_STAGES_LIST vocabulary; stages without an entry (Buyer
 * Inspection) generate nothing. */
export const WF_STAGE_TASKS: Record<string, string[]> = {
  "Contract Signed / Pending": [
    "Get deposit receipt from Title.", "Enter the deposit amount.", "Do Kiavi.",
    "Set up Inspection.", "Create a Scope of Work.", "Upload all related documents.",
    "Create Google Drive folders.",
    "Open QuickBooks for the property.",
  ],
  // Tom (2026-07-24): contractor assignment gates the START OF REHAB, not the
  // closing — you don't need crews assigned to close on a house. Moved out of
  // the contract list; the gate + generator below RE-HOME task rows that were
  // generated under the old list, so live deals follow the new rule too.
  // Tom (2026-07-28): the four things that actually happen the day a deal
  // closes. They gate the move to Rehab, same as every other stage list —
  // you shouldn't start rehab with the utilities off and no lockbox on.
  "Closed": [
    "Choose Contractors and assign them.",
    "Open Water and Electric.",
    "Put lockbox and enter the code into the CRM.",
    "Upload Closing Docs to Drive.",
    "Enter Expenses.",
  ],
  "Rehab": [
    "Add all milestones.", "Verify all documents (W9, Contracts, Insurance).",
    "Give Contractors access to the correct Drive folders.", "Insert Lender Monthly Payment.",
    "Set up staging.", "Set up Photographer.", "Set up Beycome.", "Set up Inspection.",
    "Contractor fixes the inspection issues.", "Upload photographer pictures to the Drive.",
    "Run comps again to set list price.", "Set up required buyer-agent documents.",
  ],
  "On Market": [
    "Confirm Beycome is listed.", "Confirm ShowingTime is attached, working, and responsive.",
  ],
  "Pending Sale": [
    "Add Selling Date.", "Add Inspection Date.", "Add Deposit Due.",
    "Upload buyer documents and email Title.",
  ],
  "Sold": [
    "Check QuickBooks is synced; HUD docs uploaded; expenses + income entered.",
    "Pay all credit cards related to the property.",
  ],
};

/** v1 wfGenerate: ops for the target stage's missing checklist tasks —
 * skips titles that already exist (any status) for this deal+stage. Owner
 * left blank (v1 renders blank as 'Tom'); pri Medium, due '—', st pending. */
/** An auto task whose TAGGED stage's template no longer lists its title — the
 * checklist item was re-homed to another stage (e.g. contractor assignment
 * moved Contract → Closed) AFTER this task row was generated. Such rows follow
 * the TITLE: they gate at the stage that now owns it, and they satisfy that
 * stage's generator so no duplicate row is created. */
const rehomedAuto = (t: TaskRec): boolean =>
  t.auto && t.stage !== null && !(WF_STAGE_TASKS[t.stage] ?? []).includes(t.title);

export function buildStageChecklist(dealAddress: string, stage: string, existing: TaskRec[]): RecOp[] {
  const titles = WF_STAGE_TASKS[stage] ?? [];
  if (!titles.length) return [];
  const dealKey = natKey(dealAddress);
  const have = new Set(
    existing
      .filter((t) => t.auto && t.deal !== null && natKey(t.deal) === dealKey && (t.stage === stage || rehomedAuto(t)))
      .map((t) => t.title),
  );
  return titles
    .filter((title) => !have.has(title))
    .map((title) =>
      opPutTask({
        key: taskKey(title, dealAddress, stage, true),
        title,
        status: "pending",
        due: "\u2014",
        pri: "Medium",
        deal: dealAddress,
        stage,
        auto: true,
      }),
    );
}

/** v1 wfIncomplete: the CURRENT stage's unfinished auto tasks — forward
 * moves are blocked until this is empty ("Must all be done before advancing"). */
/** Does ANY current stage template list this title? (false = a retired title —
 * those keep v1's stamped-stage behavior rather than silently un-gating). */
const titleOwnedSomewhere = (title: string): boolean =>
  Object.values(WF_STAGE_TASKS).some((l) => l.includes(title));

export function incompleteChecklist(dealAddress: string, stage: string, tasks: TaskRec[]): TaskRec[] {
  const dealKey = natKey(dealAddress);
  const titles = WF_STAGE_TASKS[stage] ?? [];
  return tasks.filter((t) => {
    if (!t.auto || t.deal === null || natKey(t.deal) !== dealKey || isTaskDone(t.status)) return false;
    // A RE-HOMED row (its stamped template no longer lists the title, but some
    // other stage's does) follows the TITLE: it stops gating its stamped stage
    // and gates the stage that now owns it — so moving an item in
    // WF_STAGE_TASKS moves the enforcement for already-generated tasks too.
    if (rehomedAuto(t) && titleOwnedSomewhere(t.title)) return titles.includes(t.title);
    return t.stage === stage; // v1 rule: tagged to the stage being left
  });
}

/** Bump when WF_STAGE_TASKS changes. Deals already SITTING in a stage never
 * saw the new titles — wfGenerate only fires on stage ENTRY — so a version
 * change triggers one backfill pass per deal+stage, remembered locally.
 * Without the marker the backfill would run on every load and resurrect any
 * auto task the user deliberately deleted. */
export const WF_TEMPLATE_VERSION = "2026-07-28-closed4";

// ---- date-driven reminders (deal closing + post-occupancy) -------------------

/** Reminders derived from a deal DATE FIELD rather than a stage move.
 * `offset` is days relative to that date (-7 = a week before). */
export interface DateReminderSpec {
  field: string;
  offset: number;
  title: string;
}

/** Tom (2026-07-28): "7 days before closing 3 days before closing 1 day before
 * closing and then same day", plus one on the last day of post-occupancy.
 * Titles are STABLE — they form the task's natural key, so a date change
 * re-dates these rows instead of stranding a second set. */
export const DATE_REMINDERS: DateReminderSpec[] = [
  { field: "t_closing", offset: -7, title: "Closing in 7 days — confirm figures and funds." },
  { field: "t_closing", offset: -3, title: "Closing in 3 days — confirm clear-to-close with Title." },
  { field: "t_closing", offset: -1, title: "Closing tomorrow — confirm time, wire, and insurance." },
  { field: "t_closing", offset: 0, title: "Closing today." },
  { field: "t_postocc", offset: 0, title: "Post occupancy ends today — take possession of the property." },
];

/** What the reminder rows for one deal SHOULD look like right now. Pure: the
 * caller supplies the date shifter so this file stays free of date parsing. */
export interface ReminderPlan {
  /** rows that don't exist yet */
  create: { key: string; title: string; due: string }[];
  /** rows whose deal date moved — same row, new due date */
  redate: { key: string; due: string }[];
  /** pending rows whose date field was CLEARED — the reminder has no meaning
   * left and would otherwise nag forever through the taskDue scheduler */
  remove: string[];
}

export function planDateReminders(
  dealAddress: string,
  fields: Record<string, unknown>,
  tasks: TaskRec[],
  shift: (value: unknown, days: number) => string,
): ReminderPlan {
  const plan: ReminderPlan = { create: [], redate: [], remove: [] };
  const dealKey = natKey(dealAddress);
  // Matched by TITLE, not by key, and only against AUTO rows: the key embeds
  // the address verbatim, so a re-cased deal address would otherwise generate
  // a second set of reminders — and a hand-written task that happens to share
  // a title must never have its due date rewritten underneath the user.
  const mine = new Map<string, TaskRec>();
  for (const t of tasks) {
    if (!t.auto || t.deal === null || natKey(t.deal) !== dealKey) continue;
    mine.set(t.title, t);
  }
  for (const spec of DATE_REMINDERS) {
    // stage "" (→ null on read) keeps these OUT of the stage checklist: they
    // must never block a stage move the way a real checklist item does.
    const key = taskKey(spec.title, dealAddress, "", true);
    const due = shift(fields[spec.field], spec.offset);
    const have = mine.get(spec.title);
    if (!due) {
      if (have && !isTaskDone(have.status)) plan.remove.push(have.natKey);
      continue;
    }
    if (!have) plan.create.push({ key, title: spec.title, due });
    else if ((have.due ?? "") !== due) plan.redate.push({ key: have.natKey, due });
  }
  return plan;
}

/** The ops for a plan, in the order they must reach the server. */
export function dateReminderOps(dealAddress: string, plan: ReminderPlan): RecOp[] {
  const ops: RecOp[] = [];
  for (const c of plan.create) {
    ops.push(opPutTask({
      key: c.key, title: c.title, status: "pending", due: c.due,
      pri: "High", deal: dealAddress, stage: "", auto: true,
    }));
  }
  for (const r of plan.redate) ops.push(opPatchTask(r.key, { due: r.due }));
  for (const k of plan.remove) ops.push(opDelTask(k));
  return ops;
}

/** Just enough of a Deal for the reconciler. Structural on purpose:
 * features/deals/api.ts already imports from THIS file, so naming its Deal
 * type here would close an import cycle. */
export interface ReconcilableDeal {
  natKey: string;
  address: string;
  stageIdx: number;
  fields: Record<string, unknown>;
}

export interface ReconcileResult {
  ops: RecOp[];
  /** rows to insert optimistically */
  added: TaskRec[];
  /** natKey → new due date */
  redated: Map<string, string>;
  /** natKeys to drop optimistically */
  removed: Set<string>;
  /** the updated deal|stage backfill markers to persist */
  done: Set<string>;
}

/** One reconciliation pass over every deal: date reminders (continuous) plus
 * the one-shot stage-checklist backfill (guarded by `done`).
 *
 * MUST reach a fixpoint — app.tsx runs this from an effect that depends on
 * `tasks`, so a pass that keeps finding work would setTasks → re-render →
 * re-plan forever. It converges because every op it emits is reflected into
 * the running `view`, so the next pass sees the world it just created.
 * tests/dealReminders.test.ts pins that in one place. */
export function reconcileDeals(
  deals: ReconcilableDeal[],
  tasks: TaskRec[],
  stageNameOf: (i: number) => string,
  shift: (value: unknown, days: number) => string,
  done: Set<string>,
): ReconcileResult {
  const ops: RecOp[] = [];
  const added: TaskRec[] = [];
  const redated = new Map<string, string>();
  const removed = new Set<string>();
  const marks = new Set(done);
  // one running view, so two deals in the same pass can't both decide a
  // shared row is missing
  let view = tasks;

  for (const d of deals) {
    const plan = planDateReminders(d.address, d.fields, view, shift);
    if (plan.create.length || plan.redate.length || plan.remove.length) {
      ops.push(...dateReminderOps(d.address, plan));
      for (const c of plan.create) {
        const row: TaskRec = {
          natKey: c.key, title: c.title, status: "pending", who: null,
          due: c.due, pri: "High", deal: d.address, stage: null, auto: true,
        };
        added.push(row);
        view = [...view, row];
      }
      for (const r of plan.redate) {
        redated.set(r.key, r.due);
        view = view.map((t) => (t.natKey === r.key ? { ...t, due: r.due } : t));
      }
      for (const k of plan.remove) {
        removed.add(k);
        view = view.filter((t) => t.natKey !== k);
      }
    }

    const stage = stageNameOf(d.stageIdx);
    const mark = d.natKey + "|" + stage;
    if (!marks.has(mark)) {
      marks.add(mark);
      for (const op of buildStageChecklist(d.address, stage, view)) {
        const row: TaskRec = {
          natKey: op.k,
          title: String((op.d as Record<string, unknown>)?.["t"] ?? ""),
          status: "pending", who: null, due: "—", pri: "Medium",
          deal: d.address, stage, auto: true,
        };
        ops.push(op);
        added.push(row);
        view = [...view, row];
      }
    }
  }
  return { ops, added, redated, removed, done: marks };
}
